import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserProfile } from '@shared/types';
import { getUserAllLocations } from '@/lib/locationUtils';
import { logger } from '@/lib/logger';
import { apiRequest } from '@/lib/fetchWithAuth';
import { useOrganization } from '@/contexts/OrganizationContext';
import { hasExecutiveLocationAccess } from '@shared/role-access';

// Define the Location type to match MemStorage
export type Location = {
  id: string;
  name: string;
  practiceName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  status: string;
  organizationId: string;
  createdAt: string;
};

// Define the context type
export interface LocationContextType {
  selectedLocationId: string;
  setSelectedLocationId: (id: string) => void;
  selectedLocationIds: string[];
  setSelectedLocationIds: (ids: string[]) => void;
  locations: Location[];
  isLoadingLocations: boolean;
  locationsOrganizationId: string | null;
  locationsError: Error | null;
  userAccessibleLocations: string[];
}

// Create the context with default values
const LocationContext = createContext<LocationContextType>({
  selectedLocationId: '',
  setSelectedLocationId: () => {},
  selectedLocationIds: [],
  setSelectedLocationIds: () => {},
  locations: [],
  isLoadingLocations: false,
  locationsOrganizationId: null,
  locationsError: null,
  userAccessibleLocations: []
});

// Define the hook first (before the provider)
export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}

// Props for the provider
interface LocationProviderProps {
  children: React.ReactNode;
  user: UserProfile;
}

// Provider component
export function LocationProvider({ children, user }: LocationProviderProps) {
  // Get all locations the user has access to
  const userAccessibleLocations = getUserAllLocations(user);
  
  // Initialize with user's primary location or first accessible location
  const initialLocationId = user.locationId || userAccessibleLocations[0] || '';
  const [selectedLocationId, setSelectedLocationId] = useState<string>(initialLocationId);
  const [selectedLocationIdsState, setSelectedLocationIdsState] = useState<string[]>(
    initialLocationId ? [initialLocationId] : []
  );
  // Track if user has manually changed the selection to avoid auto-reinitializing
  const userChangedSelectionRef = useRef<boolean>(false);

  const { currentOrganizationId, isSwitching } = useOrganization();
  
  // Fetch locations - now filtered by current organization with Agent B enhancements
  const { data: locations = [], isLoading, isSuccess, error } = useQuery<any[]>({
    queryKey: ['locations', currentOrganizationId],
    queryFn: async () => {
      console.log('🏢 LocationContext fetching locations for org:', currentOrganizationId);
      let url = '/api/locations';
      if (currentOrganizationId) {
        url += `?organizationId=${currentOrganizationId}&t=${Date.now()}`;
      } else {
        url += `?t=${Date.now()}`;
      }
      console.log('🏢 Final URL:', url);
      const response = await apiRequest<any[]>(url);
      console.log(`🏢 LocationContext received ${response?.length || 0} locations`);
      return response || [];
    },
    enabled: !!currentOrganizationId, // SIMPLIFIED: Always fetch when org is set
    staleTime: 0, // Force fresh data for multi-organization
  });

  // Reset selected locations when organization changes
  useEffect(() => {
    console.log('🔄 LocationContext: Clearing selected locations due to org change:', currentOrganizationId);
    setSelectedLocationId('');
    setSelectedLocationIdsState([]);
    // Allow initialization to run again for the new org
    userChangedSelectionRef.current = false;
  }, [currentOrganizationId]);

  // Initialize location selection based on user's assigned locations
  useEffect(() => {
    if (!locations || locations.length === 0) return;
    
    // Always initialize if no locations are selected
    if (selectedLocationIdsState.length === 0 && !userChangedSelectionRef.current) {
      
      // For CEOs, select all locations by default for better visibility
      if (hasExecutiveLocationAccess(user.role, Boolean(user.isSuperAdmin))) {
        const allLocationIds = locations.map(loc => loc.id);
        logger.info('Executive location access detected - selecting all locations by default', {
          locationCount: allLocationIds.length,
          locations: allLocationIds 
        });
        setSelectedLocationId(locations[0]?.id || '');
        setSelectedLocationIdsState(allLocationIds);
      } else if (user.locationId && locations.find(loc => loc.id === user.locationId)) {
        // For other users, default to their primary location
        logger.info('Setting initial location to user primary', { 
          locationId: user.locationId,
          userRole: user.role 
        });
        setSelectedLocationId(user.locationId);
        setSelectedLocationIdsState([user.locationId]);
      } else if (userAccessibleLocations.length > 0) {
        // Use first accessible location
        const firstLocation = userAccessibleLocations[0];
        logger.info('Setting initial location to first accessible', { 
          locationId: firstLocation,
          userRole: user.role 
        });
        setSelectedLocationId(firstLocation);
        setSelectedLocationIdsState([firstLocation]);
      } else {
        // Fallback: select first available location
        logger.info('Fallback: selecting first available location', { 
          locationId: locations[0]?.id,
          userRole: user.role 
        });
        setSelectedLocationId(locations[0]?.id || '');
        setSelectedLocationIdsState([locations[0]?.id || '']);
      }
    }
  }, [locations, user.role, user.isSuperAdmin, user.locationId, userAccessibleLocations, selectedLocationIdsState.length]);

  // Filter locations based on user access (unless executive)
  const availableLocations = React.useMemo(() => {
    if (!locations) return [];
    
    // Executives can see all locations
    if (hasExecutiveLocationAccess(user.role, Boolean(user.isSuperAdmin))) {
      return locations;
    }
    
    // Other users only see their assigned locations
    return locations.filter(loc => userAccessibleLocations.includes(loc.id));
  }, [locations, userAccessibleLocations, user.role, user.isSuperAdmin]);

  // Log the current state for debugging
  useEffect(() => {
    logger.debug('LocationContext state updated', {
      selectedLocationId,
      selectedLocationIds: selectedLocationIdsState,
      availableLocationsCount: availableLocations.length,
      userAccessibleLocations,
      userRole: user.role,
      isLoading: isLoading,
      error: null // No error object exposed here as per new_code
    });
  }, [selectedLocationId, selectedLocationIdsState, availableLocations, userAccessibleLocations, user.role, isLoading]);

  // Expose a setter that marks the selection as user-driven
  const setSelectedLocationIds = (ids: string[]) => {
    userChangedSelectionRef.current = true;
    setSelectedLocationIdsState(ids);
  };

  return (
    <LocationContext.Provider 
      value={{
        selectedLocationId,
        setSelectedLocationId,
        selectedLocationIds: selectedLocationIdsState,
        setSelectedLocationIds,
        locations: availableLocations,
        isLoadingLocations: isLoading || isSwitching,
        locationsOrganizationId: isSuccess ? currentOrganizationId : null,
        locationsError: error,
        userAccessibleLocations
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}
